import { Component, type ErrorInfo, type ReactNode } from 'react'

interface RouteErrorBoundaryProps {
  children: ReactNode
  /** Reset the boundary when this key changes (typically location.pathname). */
  resetKey: string
}

interface RouteErrorBoundaryState {
  error: Error | null
}

/**
 * Catches render errors in the active page so a bad route cannot leave the
 * shell stuck on a blank or previous screen. Resets automatically on navigation.
 */
export default class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route render error', error, info.componentStack)
  }

  componentDidUpdate(prevProps: RouteErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null })
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: '1.5rem',
            margin: '1rem',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            background: '#fef2f2',
            color: '#991b1b',
          }}
        >
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>This page failed to load</h2>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
            {this.state.error.message || 'An unexpected error occurred while rendering this page.'}
          </p>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => this.setState({ error: null })}
          >
            Try again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
